<template>
  <div v-bind="$attrs" class="step-4" v-on="$listeners">
    <div class="item-container">
      <span class="sub-title" v-text="`Programmation de l'envoi`" />
      <div class="d-flex align-center">
        <span class="input-text mr-3" v-text="'Date'" />
        <base-date-pickers @update-date="(value) => (sendingDate = value)" />
        <span class="input-text mx-3" v-text="'à'" />
        <base-time-pickers @update-time="(value) => (sendingTime = value)" />
      </div>
    </div>
    <div v-if="hasSignatories" class="datatype_field">
      <div class="d-flex flex-column sub-title">
        <span v-text="$t('folder.offer.datatypeTitle')" />
        <span class="text-line" v-text="$t('folder.offer.datatypeSubTitle')" />
      </div>
      <template v-if="defaultSignedFileStd || defaultSignedFileDatatype">
        <BaseTextField
          v-if="defaultSignedFileDatatype"
          :value="defaultSignedFileDatatype.name"
          dense
          readonly
          class="selector"
        />
        <BaseTextField
          v-if="defaultSignedFileStd"
          :value="defaultSignedFileStd.name"
          dense
          readonly
          class="selector"
        />
      </template>

      <template v-else>
        <base-autocomplete
          v-model="signedFileDatatype"
          class="selector"
          solo
          dense
          :filter="() => true"
          :search-input.sync="dataTypes.name"
          clearable
          :items="dataTypes.items"
          item-text="name"
          item-value="id"
          :loading="dataTypes.loading"
          :label="$t('folder.offer.associateDataTypeWithField')"
        >
          <template #append-item>
            <div v-intersect="loadMoreDataType" />
          </template>
        </base-autocomplete>
        <base-autocomplete
          v-model="signedFileStd"
          class="selector"
          solo
          dense
          :filter="() => true"
          :search-input.sync="templateQuestion.name"
          clearable
          :items="templateQuestion.items"
          item-text="name"
          item-value="slug"
          :loading="templateQuestion.loading"
          :label="$t('folder.offer.associateTemplateWithField')"
        >
          <template #append-item>
            <div v-intersect="loadMoreTemplateQuestions" />
          </template>
        </base-autocomplete>
      </template>
    </div>
    <!-- remove for scheduler -->
    <div class="item-container">
      <div class="d-flex flex-column sub-title">
        <span v-text="`${$t('folder.offer.relaunch_automatic')}`" />
        <span class="text-line" v-text="$t('folder.offer.label_reminder')" />
      </div>
      <div class="config-container">
        <v-switch
          v-model="reminder"
          class="checkbox"
          inset
          hide-details
          :label="$t('folder.offer.reminder_activate')"
        />
      </div>
    </div>

    <div class="item-container">
      <v-switch
        v-model="notifyImmediately"
        class="checkbox"
        inset
        hide-details
        :false-value="false"
        :true-value="true"
        :label="$t('utils.notify_immediately')"
      />
      <template v-if="notifyImmediately">
        <div class="sub-title mb-1" v-text="$t('utils.notification_message')" />
        <BaseMarkdownEditor v-model="notifyMessage" :with-link="false" />
      </template>
    </div>

    <div class="action-container">
      <BaseButton
        class="ml-auto"
        type="primary"
        :disabled="isLoading"
        outlined
        @click="goBackStep"
      >
        <Base-icon small icon="$mdiArrowLeftThick" />
        <span class="ml-2" v-text="$t('utils.back')" />
      </BaseButton>
      <BaseButton type="primary" :loading="isLoading" @click="validate">
        <span class="mr-2" v-text="$t('utils.send')" />
        <Base-icon small icon="$mdiArrowRightThick" />
      </BaseButton>
    </div>
  </div>
</template>

<script>
import { validationMixin } from "vuelidate";
import DatatypeAPI from "@/core/Services/datatype.api";
import { fetchQuestionTemplates } from "@/core/Services/questionTemplate.service";
import Datatype from "@/core/Models/Datatype";
import { mapState } from "vuex";
export default {
  name: "FolderOfferStep4",
  mixins: [validationMixin],
  props: {
    defaultSignedFileDatatype: {
      type: Object,
      required: false,
      default: () => undefined,
    },
    defaultSignedFileStd: {
      type: Object,
      required: false,
      default: () => undefined,
    },
    isLoading: {
      type: Boolean,
    },
  },
  data() {
    return {
      sendingDate: null,
      sendingTime: null,
      configTypes: [
        { title: "Une fois par jour", value: "1" },
        { title: "Tous les 2 jours", value: "2" },
        { title: "Une fois par semaine", value: "7" },
        { title: "Toutes les 2 semaines", value: "14" },
      ],
      scheduler: null,
      reminder: true,
      notifyImmediately: true,
      notifyMessage: "",
      signedFileDatatype: null,
      signedFileStd: null,
      fetchingDatatypes: false,
      dataTypes: {
        items: [],
        pageSize: 10,
        page: 1,
        next: null,
        name: undefined,
      },
      templateQuestion: {
        items: [],
        pageSize: 10,
        page: 1,
        next: null,
        name: undefined,
      },
    };
  },
  validations() {
    let validations = {};
    validations.sendingDate = {
      minValue: (value) => new Date(value) > new Date(),
    };
    return validations;
  },
  computed: {
    ...mapState({
      step: (state) => state.offer.offerStep,
      newOffer: (state) => state.offer.newOffer,
    }),
    hasSignatories() {
      if (this.newOffer.type != "SIGNATURE") return false;
      return this.newOffer.signatories?.some(
        (item) => item.action === "signer"
      );
    },
  },
  watch: {
    defaultSignedFileDatatype: {
      handler(value) {
        this.signedFileDatatype = value ? value.id : undefined;
      },
      deep: true,
      immediate: true,
    },
    notifyImmediately(val) {
      if (val && !this.notifyMessage) {
        this.notifyMessage = this.newOffer.description || "";
      }
    },
    "newOffer.description": {
      immediate: true,
      handler(val) {
        if (this.notifyImmediately && !this.notifyMessage) {
          this.notifyMessage = val || "";
        }
      },
    },
    "dataTypes.name"(newVal, oldVal) {
      if (newVal == oldVal || oldVal == null) return;
      this.filterDataType();
    },
    "templateQuestion.name"(newVal, oldVal) {
      if (newVal == oldVal || oldVal == null) return;
      this.filterQuestionTemplate();
    },
  },
  created() {
    this.fetchDatatypes();
    this.fetchQuestionTemplate();
  },
  methods: {
    async filterQuestionTemplate() {
      this.templateQuestion.page = 1;
      this.templateQuestion.items = [];
      await this.fetchQuestionTemplate();
    },
    async filterDataType() {
      this.dataTypes.page = 1;
      this.dataTypes.items = [];
      await this.fetchDatatypes();
    },
    async fetchQuestionTemplate() {
      if (!this.templateQuestion.page) return;
      this.templateQuestion.loading = true;
      try {
        const response = await fetchQuestionTemplates({
          organizationId: this.$route.params.organizationId,
          page: this.templateQuestion.page,
          pageSize: this.templateQuestion.pageSize,
          name: this.templateQuestion.name,
          typeQuestion: ["MULTIPLE_FILE"],
        });
        this.templateQuestion = {
          ...this.templateQuestion,
          ...response,
          items: [...this.templateQuestion.items, ...response.items],
        };
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.templateQuestion.loading = false;
    },
    async fetchDatatypes() {
      if (!this.dataTypes.page) return;
      this.dataTypes.loading = true;
      try {
        const response = await DatatypeAPI.getDatatypes({
          page: this.dataTypes.page,
          name: this.dataTypes.name,
          pageSize: this.dataTypes.pageSize,
          typeQuestion: ["MULTIPLE_FILE"],
        });
        this.dataTypes = {
          ...this.dataTypes,
          items: [
            ...this.dataTypes.items,
            ...response.data.results.map((d) => new Datatype(d)),
          ],
          next: response.data.next,
        };
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.dataTypes.loading = false;
    },

    async loadMoreTemplateQuestions(_, __, isIntersecting) {
      if (!isIntersecting) return;
      this.templateQuestion.page = this.templateQuestion.next;
      await this.fetchQuestionTemplate();
    },
    async loadMoreDataType(_, __, isIntersecting) {
      if (!isIntersecting) return;
      this.dataTypes.page = this.dataTypes.next;
      await this.fetchDatatypes();
    },
    goBackStep() {
      this.$store.commit("offer/setOfferStep", +this.step - 1);
    },
    validate() {
      let payload = {};
      if (!this.$v.sendingDate.$invalid || this.sendingTime) {
        payload.publishDate = new Date(
          `${this.sendingDate} ${this.sendingTime}`
        ).toISOString();
      }
      this.scheduler && (payload.scheduler = this.scheduler);
      this.$store.commit("offer/setNewOffer", {
        ...this.newOffer,
        auto_reminder: this.reminder,
        notifyImmediately: this.notifyImmediately,
        notifyMessage: this.notifyMessage,
        ...payload,
        signedFileDatatype: this.signedFileDatatype,
        signedFileStd: this.defaultSignedFileStd
          ? this.defaultSignedFileStd?.slug
          : this.signedFileStd,
      });
      this.$emit("create-offer");
    },
  },
};
</script>

<style lang="scss" scoped>
.step-4 {
  display: flex;
  flex-direction: column;
}
.item-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0 24px;
  margin-bottom: 24px;
}
.config {
  flex: 1;
}
.sub-title {
  color: var(--principale-gris-noir, #353542);
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}
.config-container {
  display: flex;
  align-items: center;
  height: 76px;
  gap: 24px;
  width: 100%;
}
.text-line {
  font-size: 14px;
  margin-top: 10px;
  font-weight: 400;
}
.input-text {
  font-family: "Inter";
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: #707080;
}
.action-container {
  display: flex;
  align-items: center;
  margin-top: auto;
  gap: 12px;
}
.checkbox {
  margin: 0 0 16px 0 !important;
  .checkbox_label {
    display: flex;
    align-items: center;
    & > * {
      margin-right: 8px;
    }
  }
}
.datatype_field {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0 24px;
  .selector {
    width: 300px;
  }
}
</style>
